import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import '../styles/cookieconsent-custom.css';

const initializeHotjar = () => {
        if (window.hj) return;

        (function (h, o, t, j, a, r) {
                h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments) };
                h._hjSettings = { hjid: 6552579, hjsv: 6 };
                a = o.getElementsByTagName('head')[0];
                r = o.createElement('script'); r.async = 1;
                r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
                a.appendChild(r);
        })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
};

const removeHotjar = () => {
        document.cookie.split(';').forEach(cookie => {
                const cookieName = cookie.split('=')[0].trim();
                if (cookieName.startsWith('_hj')) {
                        document.cookie = cookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                }
        });

        if (window.hj) {
                delete window.hj;
        }
};

export const useCookieConsent = () => {
        useEffect(() => {
                CookieConsent.run({
                        cookie: {
                                name: 'cc_cookie',
                                expiresAfterDays: 365,
                        },

                        // Automatically show consent modal on first visit
                        guiOptions: {
                                consentModal: {
                                        layout: 'box',
                                        position: 'bottom right',
                                        equalWeightButtons: false,
                                        flipButtons: false
                                },
                                preferencesModal: {
                                        layout: 'box',
                                        position: 'right',
                                        equalWeightButtons: true,
                                        flipButtons: false
                                }
                        },

                        // Categories of cookies
                        categories: {
                                necessary: {
                                        enabled: true,
                                        readOnly: true,
                                },
                                analytics: {
                                        enabled: false,
                                        autoClear: {
                                                cookies: [
                                                        {
                                                                name: /^_hj/
                                                        }
                                                ]
                                        },
                                        services: {
                                                hotjar: {
                                                        label: 'Hotjar',
                                                        onAccept: () => initializeHotjar(),
                                                        onReject: () => removeHotjar()
                                                }
                                        }
                                }
                        },

                        language: {
                                default: 'en',
                                autoDetect: 'browser',
                                translations: {
                                        en: {
                                                consentModal: {
                                                        title: 'Hi plant-enthusiast, it\'s cookie time!',
                                                        description: 'We use cookies to enhance your browsing experience and analyze our traffic. By clicking "Accept all", you consent to our use of cookies. You can also customize your preferences.',
                                                        acceptAllBtn: 'Accept all',
                                                        acceptNecessaryBtn: 'Reject all',
                                                        showPreferencesBtn: 'Manage preferences',
                                                        footer: '<a href="/privacy-policy">Privacy Policy</a> <a href="/terms-of-service">Terms of Service</a>'
                                                },
                                                preferencesModal: {
                                                        title: 'Cookie Preferences',
                                                        acceptAllBtn: 'Accept all',
                                                        acceptNecessaryBtn: 'Reject all',
                                                        savePreferencesBtn: 'Save preferences',
                                                        closeIconLabel: 'Close',
                                                        serviceCounterLabel: 'Service|Services',
                                                        sections: [
                                                                {
                                                                        title: 'Cookie Usage',
                                                                        description: 'We use cookies to improve your experience on our website. You can choose which categories of cookies you want to allow below.'
                                                                },
                                                                {
                                                                        title: 'Essential Cookies <span class="pm__badge">Always Enabled</span>',
                                                                        description: 'These cookies are necessary for the website to function properly. They enable core functionality such as authentication, security, and session management. The website cannot function properly without these cookies.',
                                                                        linkedCategory: 'necessary'
                                                                },
                                                                {
                                                                        title: 'Analytics Cookies',
                                                                        description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. We use Hotjar to analyze website usage and improve our service.',
                                                                        linkedCategory: 'analytics',
                                                                        cookieTable: {
                                                                                headers: {
                                                                                        name: 'Cookie',
                                                                                        domain: 'Domain',
                                                                                        desc: 'Description',
                                                                                        duration: 'Duration'
                                                                                },
                                                                                body: [
                                                                                        {
                                                                                                name: '_hjSessionUser_*',
                                                                                                domain: 'hotjar.com',
                                                                                                desc: 'Hotjar cookie that is set when the customer first lands on a page',
                                                                                                duration: '1 year'
                                                                                        },
                                                                                        {
                                                                                                name: '_hjSession_*',
                                                                                                domain: 'hotjar.com',
                                                                                                desc: 'Holds current session data',
                                                                                                duration: '30 minutes'
                                                                                        },
                                                                                        {
                                                                                                name: '_hjIncludedInSessionSample_*',
                                                                                                domain: 'hotjar.com',
                                                                                                desc: 'Set to determine if a user is included in the data sampling',
                                                                                                duration: '30 minutes'
                                                                                        }
                                                                                ]
                                                                        }
                                                                },
                                                                {
                                                                        title: 'More information',
                                                                        description: 'For any queries in relation to our policy on cookies and your choices, please <a href="mailto:info@moveinnovation.dk">contact us</a>.'
                                                                }
                                                        ]
                                                }
                                        }
                                }
                        },

                        onFirstConsent: ({ cookie }) => {
                                console.log('Cookie consent given:', cookie);
                        },

                        onConsent: ({ cookie }) => {
                                if (cookie.categories.includes('analytics')) {
                                        initializeHotjar();
                                }
                        },

                        onChange: ({ cookie, changedCategories }) => {
                                if (changedCategories.includes('analytics')) {
                                        if (cookie.categories.includes('analytics')) {
                                                initializeHotjar();
                                        } else {
                                                removeHotjar();
                                        }
                                }
                        }
                });
        }, []);
};

export const showCookiePreferences = () => {
        CookieConsent.showPreferences();
};

export const isCategoryAccepted = (category) => {
        const cookie = CookieConsent.getCookie();
        return cookie?.categories?.includes(category) || false;
};
